import { useState, useEffect } from "react";
import { useHomeownerAuth } from "./useHomeownerAuth";
import HomeownerGate from "./HomeownerGate";
import SignatureCanvas from "./SignatureCanvas";
import { supabaseAnon as supabase } from "../../lib/supabase";
import {
  INSTALL_AGREEMENT_TERMS,
  RETAIL_AGREEMENT_TERMS,
  AGREEMENT_EXPECTATIONS,
  agreementPartiesPreamble,
  WORK_NOT_DONE_ACK,
  WORK_NOT_DONE_DEPRECIATION,
  WORK_NOT_DONE_HEADING,
  WORK_NOT_DONE_INTRO,
} from "../../lib/installAgreementTerms";
import { groupPicks, lineMeta, pickTotals, readPicks, type PickedLine } from "../../lib/scopeLines";
import { authTriple, hoSignAgreement, hoAttachSignedPdf, hoUpdateContactEmail, hoPortalNotify } from "../../lib/homeownerRpc";
import { afterBuildCredit, buildAgreementPayload, POST_AGREEMENT_STAGES, readTradeLines, type AgreementTradeLine } from "../../lib/agreementPayload";
import type { Deal } from "../../lib/types";
import { DRIP_EDGE_COLOR_NAMES, matchDripEdgeColor, summarizeColorSelections } from "../../lib/colorOptions";

// ---------------------------------------------------------------------------
// Normalize agreement_data from edge function into a consistent shape
// The generate-agreement function stores financials at the top level,
// but our components expect a nested structure. This adapter handles both.
// ---------------------------------------------------------------------------

interface NormalizedAgreement {
  scope_items: { line_number: number; description: string; category: string; rcv: number; acv: number; depreciation: number }[];
  excluded_items: { line_number: number; description: string; category: string; rcv: number; acv: number; depreciation: number }[];
  retail_addons: { description: string; amount: number }[];
  financials: {
    rcv_total: number;
    acv_total: number;
    depreciation_total: number;
    recoverable_depreciation: number;
    non_recoverable_depreciation: number;
    deductible: number;
    pwi_code_amount: number;
    excluded_acv_total: number;
    excluded_rcv_total: number;
    retail_addons_total: number;
    misc_credit: number;
    net_deductible: number;
    out_of_pocket: number;
    initial_invoice_amount: number;
    // Two-settlement breakdown (Apr 27 redesign — populated for new flow,
    // falls back to legacy single-figure math when missing).
    upgrades: number;
    retail_profit: number;
    at_cost_retail: number;
    acv_credit: number;
    dep_credit: number;
    supplements: number;
    acv_settlement: number;            // ACV − Deductible
    acv_settlement_to_bardin: number;  // = ACV settlement (May 7, 2026 — HO endorses full check; un-perf ACV credits the deductible, not the ACV)
    first_payment_to_bardin: number;   // What HO pays before build (ACV-to-Bardin + HO OOP)
    depreciation_settlement: number;   // Released after build
    insurance_pays: number;            // ACV-to-Bardin + Dep settlement
    credits_applied: number;
    /** Credits beyond the deductible: they come off the payment after the build. */
    credit_overage: number;
    acv_only: boolean;
  };
  product_line: string;
  /** Oct 30, 2026 (scope gate): the carrier lines Bardin won't do, as the RM
   *  ticked them. An agreement generated before then carries none. */
  not_performing: PickedLine[];
  // Retail option (Jun 11, 2026) — when true, the Financial Summary renders the
  // 50/50 retail schedule instead of the insurance two-settlement breakdown.
  is_retail: boolean;
  retail_contract_total: number;
  retail_deposit: number;
  retail_completion_payment: number;
  retail_scope_description: string | null;
  // Oct 2, 2026 — homeowner add-ons with their customer-facing description.
  // Oct 29, 2026: a trade billed to the homeowner rides here too, flagged due
  // at completion (it is not in the out-of-pocket / first payment).
  add_ons: Array<{ label: string; amount: number; description: string; dueAtCompletion?: boolean }>;
  // Oct 29, 2026 — trades billed to the homeowner from the trade sheet: one
  // customer line each (never the floor or the sub's price), due at completion.
  // An agreement generated before then carries none.
  trade_lines: AgreementTradeLine[];
  trade_total: number;
  /** What is due after the build (insurance: depreciation + the trades, less
   *  any credit beyond the deductible; retail: the other 50% + the trades). */
  completion_payment: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeAgreement(raw: any): NormalizedAgreement | null {
  if (!raw) return null;

  // Handle nested financials (our typed interface) vs flat top-level (edge function output)
  const fin = raw.financials ?? raw;

  const rcv = fin.rcv_total ?? fin.total_rcv ?? 0;
  const acv = fin.acv_total ?? fin.total_acv ?? 0;
  const dep = fin.depreciation_total ?? fin.total_depreciation ?? 0;
  const ded = fin.deductible ?? 0;
  // ScopeSection writes `pwi_code` (matching the deals column name); legacy
  // generate-agreement edge function wrote `pwi_code_amount`. Read both so the
  // Carrier Scope Reference PWI row and the depreciation breakdown can render.
  const pwi = fin.pwi_code_amount ?? fin.pwi_code ?? fin.code_upgrade_rcv ?? 0;
  const exAcv = fin.excluded_acv_total ?? fin.excluded_acv ?? 0;
  const exRcv = fin.excluded_rcv_total ?? fin.excluded_rcv ?? 0;
  const retailTotal = fin.retail_addons_total ?? fin.retail_total ?? 0;
  const misc = fin.misc_credit ?? raw.misc_credit ?? 0;
  const netDed = fin.net_deductible ?? raw.net_deductible ?? Math.max(0, ded - exAcv);
  // ScopeSection/buildAgreementPayload write `ho_out_of_pocket` (matches scopeCalc
  // shape) — when present it is authoritative EVEN AT $0, because credits can
  // legitimately zero out the deductible (Jul 30, 2026: Dowling — acv_credit >
  // deductible). The recompute-when-zero hack below only guards the legacy
  // generate-agreement fields (`out_of_pocket` / `homeowner_oop`), which had a
  // known bug writing 0; that legacy formula predates acv_credit and must never
  // run against a new-flow payload.
  const oopCalc = Math.max(0, netDed + retailTotal - misc);
  const modernOop = fin.ho_out_of_pocket ?? raw.ho_out_of_pocket;
  const legacyOop = fin.out_of_pocket ?? fin.homeowner_oop ?? raw.homeowner_oop ?? 0;
  const oop = modernOop != null ? Number(modernOop) || 0 : legacyOop > 0 ? legacyOop : oopCalc;

  const scopeItems = (raw.scope_items ?? raw.included_items ?? []).map((it: any) => ({
    line_number: it.line_number ?? 0,
    description: it.description ?? "",
    category: it.category ?? "",
    rcv: it.rcv ?? 0,
    acv: it.acv ?? 0,
    depreciation: it.depreciation ?? 0,
  }));

  const excludedItems = (raw.excluded_items ?? []).map((it: any) => ({
    line_number: it.line_number ?? 0,
    description: it.description ?? "",
    category: it.category ?? "",
    rcv: it.rcv ?? 0,
    acv: it.acv ?? 0,
    depreciation: it.depreciation ?? 0,
  }));

  const retailAddons = (raw.retail_addons ?? []).map((it: any) => ({
    description: it.description ?? "",
    amount: it.amount ?? it.price ?? 0,
  }));

  // Two-settlement fields (Apr 27 redesign). New-flow snapshots populate these;
  // legacy snapshots leave them undefined and we fall back to derived values.
  const upgrades = fin.upgrades ?? 0;
  const retailProfit = fin.retail_profit ?? 0;
  const atCostRetail = fin.at_cost_retail ?? 0;
  const acvCredit = fin.acv_credit ?? 0;
  const depCredit = fin.dep_credit ?? 0;
  const supplements = fin.supplements ?? 0;
  const acvOnly = fin.acv_only === true;

  // ACV settlement = what insurance writes the HO before construction (PWI is NOT part of this check).
  // May 7, 2026: ACV credit no longer subtracted from acvSettlementToBardin — it only credits the
  // deductible. Fallback for legacy snapshots updated to match.
  const acvSettlement = fin.acv_settlement ?? Math.max(0, acv - ded);
  const acvSettlementToBardin = fin.acv_settlement_to_bardin ?? Math.max(0, acvSettlement);
  // Depreciation settlement = released after build (zero if ACV-only)
  const depSettlement = fin.depreciation_settlement ?? (acvOnly ? 0 : Math.max(0, dep - depCredit) + supplements + pwi);
  const insurancePays = fin.insurance_pays ?? (acvSettlementToBardin + depSettlement);
  // First payment to Bardin = HO endorses insurance ACV check + writes their OOP check
  const firstPayment = fin.first_payment_to_bardin ?? (acvSettlementToBardin + oop);
  const creditsApplied = fin.credits_applied ?? Math.min(acvCredit + misc, ded);
  const creditOverage = Number(fin.credit_overage ?? raw.credit_overage ?? Math.max(0, acvCredit + misc - ded)) || 0;
  // Jun 8, 2026: legacy-snapshot fallback aligned with the corrected scopeCalc
  // identity — initial invoice = First Payment + Depreciation Settlement (all
  // homeowner credits, incl. misc, are already netted into firstPayment). The
  // old `+ creditsApplied` term double-counted credits and overstated the total.
  const initialInvoice = fin.initial_invoice ?? fin.initial_invoice_amount ?? Math.max(0, firstPayment + depSettlement - creditOverage);

  // Retail option (Jun 11, 2026): new-flow snapshots carry the flag + amounts;
  // derive the 50/50 split defensively if only the total is present.
  const isRetail = raw.is_retail === true;
  const retailContractTotal = Number(raw.retail_contract_total ?? 0) || 0;
  const retailDeposit = Number(raw.retail_deposit ?? 0) || retailContractTotal / 2;
  const retailCompletion = Number(raw.retail_completion_payment ?? 0) || retailContractTotal - retailDeposit;

  // Trades billed to the homeowner (Oct 29, 2026). A total with no readable
  // lines prints as one line so the amounts still add up.
  const tradeTotal = Math.max(0, Number(raw.trade_ho_total ?? 0) || 0);
  const tradeLinesRead = tradeTotal > 0 ? readTradeLines(raw.trade_lines) : [];
  const tradeLines = tradeLinesRead.length === 0 && tradeTotal > 0
    ? [{ label: "Other trades", amount: tradeTotal }]
    : tradeLinesRead;
  const completionPayment = raw.completion_payment != null && Number.isFinite(Number(raw.completion_payment))
    ? Number(raw.completion_payment)
    : isRetail ? retailCompletion : Math.max(0, initialInvoice - firstPayment);

  return {
    scope_items: scopeItems,
    excluded_items: excludedItems,
    retail_addons: retailAddons,
    is_retail: isRetail,
    retail_contract_total: retailContractTotal,
    retail_deposit: retailDeposit,
    retail_completion_payment: retailCompletion,
    retail_scope_description: raw.retail_scope_description ?? null,
    add_ons: [
      { label: "Upgrades", amount: upgrades, description: String(raw.upgrades_description ?? "").trim() },
      { label: "Additional work", amount: retailProfit, description: String(raw.retail_profit_description ?? "").trim() },
      { label: "Additional trades (at cost)", amount: atCostRetail, description: String(raw.at_cost_retail_description ?? "").trim() },
    ].filter((a) => a.amount > 0)
      .concat(tradeLines.map((t) => ({ label: t.label, amount: t.amount, description: "", dueAtCompletion: true }))),
    trade_lines: tradeLines,
    trade_total: tradeTotal,
    completion_payment: completionPayment,
    financials: {
      rcv_total: rcv,
      acv_total: acv,
      depreciation_total: dep,
      recoverable_depreciation: dep, // assume all recoverable unless split provided
      non_recoverable_depreciation: 0,
      deductible: ded,
      pwi_code_amount: pwi,
      excluded_acv_total: exAcv,
      excluded_rcv_total: exRcv,
      retail_addons_total: retailTotal,
      misc_credit: misc,
      net_deductible: netDed,
      out_of_pocket: oop,
      initial_invoice_amount: initialInvoice,
      // Two-settlement breakdown
      upgrades,
      retail_profit: retailProfit,
      at_cost_retail: atCostRetail,
      acv_credit: acvCredit,
      dep_credit: depCredit,
      supplements,
      acv_settlement: acvSettlement,
      acv_settlement_to_bardin: acvSettlementToBardin,
      first_payment_to_bardin: firstPayment,
      depreciation_settlement: depSettlement,
      insurance_pays: insurancePays,
      credits_applied: creditsApplied,
      credit_overage: creditOverage,
      acv_only: acvOnly,
    },
    product_line: raw.product_line ?? "Full Roof Replacement — Owens Corning Duration TruDef",
    not_performing: isRetail ? [] : readPicks(raw.items_not_performing_detail),
  };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SHINGLE_COLORS = [
  "Black Sable",
  "Brownwood",
  "Chateau Green",
  "Desert Tan",
  "Driftwood",
  "Estate Gray",
  "Harbor Blue",
  "Merlot",
  "Onyx Black",
  "Quarry Gray",
  "Sand Dune",
  "Sierra Gray",
  "Storm Cloud",
  "Teak",
  "Williamsburg Gray",
];

// Drip edge comes in three colors (Oct 22, 2026, per Greg). One list feeds
// every picker: src/lib/colorOptions.ts.
const DRIP_EDGE_COLORS: readonly string[] = DRIP_EDGE_COLOR_NAMES;

const DEFAULT_SCOPE = [
  "Tear-off & disposal",
  "Synthetic underlayment",
  "Ice & water shield",
  "Drip edge",
  "New shingles",
  "Ridge vent",
  "Pipe boots",
  "Step flashing",
  "Starter strip",
];

// ---------------------------------------------------------------------------
// T&C content
// ---------------------------------------------------------------------------
//
// On-screen T&C is rendered from the SAME source as the install agreement PDF
// (src/lib/installAgreementTerms.ts). When that file changes, both the on-screen
// reading and the signed PDF stay in sync — no risk of the HO reading one set
// of terms and signing a different set.

// ---------------------------------------------------------------------------
// Expectations
// ---------------------------------------------------------------------------

// Source of truth is installAgreementTerms.ts so the signed PDF prints the
// same list the homeowner checks off here.
const EXPECTATIONS = AGREEMENT_EXPECTATIONS;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(n: number | null | undefined): string {
  if (n == null) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

// ---------------------------------------------------------------------------
// Signature surface — the shared SignatureCanvas component (Draw + Type +
// full-screen modes) is imported at the top of the file. The inline
// definition that used to live here was extracted on May 11, 2026 so the
// Install Agreement and the Color Confirmation Addendum stay in sync.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FinancialSummaryCard({ data }: { data: NormalizedAgreement }) {
  const f = data.financials;

  // Retail (Jun 11, 2026): fixed contract price, 50% deposit / 50% completion.
  // No insurance settlements, no carrier scope reference.
  if (data.is_retail) {
    return (
      <div className="bg-white rounded-xl p-5 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-bardin-teal flex items-center gap-2">
            <span className="text-base">💰</span> Financial Summary
          </h3>
          <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-teal-100 text-teal-800">Retail</span>
        </div>

        <div className="bg-gradient-to-br from-bardin-orange/10 to-bardin-peach/30 rounded-xl border-2 border-bardin-orange/30 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-bardin-orange">
            Contract Total
          </div>
          <div className="mt-1 text-3xl font-bold text-bardin-ink">{fmt(data.retail_contract_total)}</div>
          <div className="text-xs text-slate-600 mt-2">
            Fixed price for the full scope of work — no insurance claim involved.
          </div>
          {data.trade_total > 0 && (
            <div className="text-xs text-slate-700 mt-2 pt-2 border-t border-bardin-orange/20">
              + Trades billed to you (due at completion): <span className="font-semibold">{fmt(data.trade_total)}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-orange-50 border border-bardin-orange/40 rounded-lg p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-bardin-orange mb-1">
              Before Build
            </div>
            <div className="text-sm font-semibold text-bardin-ink">50% Deposit</div>
            <div className="text-2xl font-bold text-bardin-orange mt-1">{fmt(data.retail_deposit)}</div>
            <div className="text-xs text-slate-500 mt-1">Due at signing or before material delivery, whichever is later.</div>
          </div>
          <div className="bg-teal-50 border border-bardin-teal/40 rounded-lg p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-bardin-teal mb-1">
              After Build
            </div>
            <div className="text-sm font-semibold text-bardin-ink">{data.trade_total > 0 ? "Final Payment" : "50% Final Payment"}</div>
            <div className="text-2xl font-bold text-bardin-teal mt-1">{fmt(data.retail_completion_payment)}</div>
            <div className="text-xs text-slate-500 mt-1">
              {data.trade_total > 0
                ? `The other 50% of the contract (${fmt(data.retail_contract_total - data.retail_deposit)}) + the trades billed to you (${fmt(data.trade_total)}), due upon completion of the work.`
                : "Due upon completion of the work."}
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-400 leading-relaxed">
          The deposit is collected before construction starts. The final payment is due when the work is complete.
        </p>
      </div>
    );
  }

  // Show the two-settlement breakdown only when we have meaningful new-flow
  // numbers to display. Legacy agreements that only have OOP fall through
  // to the simple summary at the bottom.
  const hasTwoSettlement = f.first_payment_to_bardin > 0 || f.depreciation_settlement > 0 || f.acv_settlement > 0;
  // A credit beyond the deductible comes off the payment after the build: off the trades first, then off the
  // depreciation, never below $0 — the same split and the same rows as the agreement PDF and the portal's payment
  // schedule, so the page the homeowner signs shows the PDF's numbers (second review, Oct 30, 2026).
  const afterCredit = afterBuildCredit(f.credit_overage, data.trade_total, f.acv_only ? 0 : f.depreciation_settlement);

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-bardin-teal flex items-center gap-2">
          <span className="text-base">💰</span> Financial Summary
        </h3>
        {f.acv_only && (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800">ACV only</span>
        )}
      </div>

      {hasTwoSettlement && (
        <>
          {/* Hero — what HO writes the check for before construction starts */}
          <div className="bg-gradient-to-br from-bardin-orange/10 to-bardin-peach/30 rounded-xl border-2 border-bardin-orange/30 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-bardin-orange">
              Collect Before Build
            </div>
            <div className="mt-1 text-3xl font-bold text-bardin-ink">{fmt(f.first_payment_to_bardin)}</div>
            <div className="text-xs text-slate-600 mt-2">
              You'll endorse the insurance ACV check ({fmt(f.acv_settlement_to_bardin)}) over to Bardin Roofing
              and write Bardin a separate check for your out-of-pocket portion ({fmt(f.out_of_pocket)}) before construction begins.
            </div>
          </div>

          {/* Two-side breakdown: Insurance pays | Your out-of-pocket */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* INSURANCE side */}
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-sky-700 mb-2">
                Money From Insurance
              </div>
              <div className="space-y-1 text-sm">
                <Row label="ACV settlement (endorsed to Bardin)" value={fmt(f.acv_settlement_to_bardin)} />
                <div className="border-t border-sky-200 my-1.5" />
                {f.acv_only ? (
                  <div className="text-xs text-amber-700 italic">
                    No depreciation settlement — insurance is paying ACV only.
                  </div>
                ) : (
                  <>
                    {/* Depreciation Settlement breakdown — show the components so
                        the homeowner can reconcile against the carrier scope. */}
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-sky-700 pt-0.5">
                      Depreciation Settlement (after build)
                    </div>
                    <Row label="Recoverable Depreciation" value={fmt(f.recoverable_depreciation || f.depreciation_total)} sub />
                    {f.pwi_code_amount > 0 && (
                      <Row label="+ PWI (paid when invoiced)" value={`+${fmt(f.pwi_code_amount)}`} sub />
                    )}
                    {f.supplements > 0 && (
                      <Row label="+ Approved supplements" value={`+${fmt(f.supplements)}`} sub />
                    )}
                    {f.dep_credit > 0 && (
                      <Row label="− Un-perf depreciation" value={`-${fmt(f.dep_credit)}`} sub green />
                    )}
                    {afterCredit.onInsurance > 0 && (
                      <Row label="− Credit beyond your deductible" value={`-${fmt(afterCredit.onInsurance)}`} sub green />
                    )}
                    <div className="flex items-center justify-between pt-0.5 pl-2 border-t border-dashed border-sky-200 mt-0.5">
                      <span className="text-xs font-semibold text-sky-900">Depreciation Settlement</span>
                      <span className="text-xs font-semibold text-sky-900">{fmt(f.depreciation_settlement - afterCredit.onInsurance)}</span>
                    </div>
                  </>
                )}
                <div className="border-t border-sky-300 my-1.5" />
                <div className="flex items-center justify-between pt-0.5">
                  <span className="text-xs font-bold text-sky-900">Insurance pays Bardin</span>
                  <span className="text-base font-bold text-sky-900">{fmt(f.insurance_pays - afterCredit.onInsurance)}</span>
                </div>
              </div>
            </div>

            {/* HOMEOWNER side */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 mb-2">
                Your Out-of-Pocket
              </div>
              <div className="space-y-1 text-sm">
                <Row label="Deductible" value={fmt(f.deductible)} />
                {f.upgrades > 0 && <Row label="+ Upgrades" value={`+${fmt(f.upgrades)}`} />}
                {f.retail_profit > 0 && <Row label="+ Retail add-ons" value={`+${fmt(f.retail_profit)}`} />}
                {f.at_cost_retail > 0 && <Row label="+ At-cost retail" value={`+${fmt(f.at_cost_retail)}`} />}
                {f.credits_applied > 0 && (
                  <Row label="− Credits applied" value={`-${fmt(f.credits_applied)}`} sub green />
                )}
                <div className="border-t border-emerald-300 my-1.5" />
                <div className="flex items-center justify-between pt-0.5">
                  <span className="text-xs font-bold text-emerald-900">You pay Bardin{data.trade_total > 0 ? " (before build)" : ""}</span>
                  <span className="text-base font-bold text-emerald-900">{fmt(f.out_of_pocket)}</span>
                </div>
                {/* Oct 29, 2026 — trades billed to the homeowner from the trade
                    sheet: due at completion, not in the first payment. */}
                {data.trade_total > 0 && (
                  <>
                    <div className="border-t border-emerald-200 my-1.5" />
                    <Row label="+ Trades billed to you (due at completion)" value={`+${fmt(data.trade_total)}`} />
                    {data.trade_lines.map((t, i) => (
                      <Row key={i} label={t.label} value={fmt(t.amount)} sub />
                    ))}
                    {afterCredit.onTrades > 0 && (
                      <Row label="− Credit beyond your deductible" value={`-${fmt(afterCredit.onTrades)}`} sub green />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Initial invoice — what Bardin invoices the project for, total */}
          <div className="bg-bardin-sky rounded-lg px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-bardin-teal">Total Project Invoice</div>
                {/* Oct 30, 2026 (scope gate): the old "+ credits applied" wording predates the Jun 8
                    identity (credits are already in the first payment). A credit beyond the deductible
                    comes off the payment after the build, inside the boxes above, so they add up to this. */}
                <div className="text-[11px] text-slate-500 mt-0.5">
                  First payment + depreciation settlement
                  {data.trade_total > 0 && " + trades billed to you (due at completion)"}
                </div>
              </div>
              <div className="text-xl font-bold text-bardin-teal">{fmt(f.initial_invoice_amount)}</div>
            </div>
          </div>
        </>
      )}

      {/* Carrier-scope reference numbers — always shown for context */}
      <div className="bg-slate-50 rounded-lg p-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Carrier Scope Reference
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <Row label="Insurance RCV" value={fmt(f.rcv_total)} />
          <Row label="ACV" value={fmt(f.acv_total)} />
          <Row label="Recoverable Depreciation" value={fmt(f.recoverable_depreciation)} />
          <Row label="Your Deductible" value={fmt(f.deductible)} />
          {f.pwi_code_amount > 0 && <Row label="PWI / Code Upgrades" value={fmt(f.pwi_code_amount)} />}
          {f.supplements > 0 && <Row label="Approved Supplements" value={fmt(f.supplements)} />}
        </div>
      </div>

      {/* Legacy fallback — shown only when two-settlement data isn't present */}
      {!hasTwoSettlement && (
        <div className="bg-bardin-sky rounded-lg px-3 py-2.5 flex items-center justify-between">
          <span className="font-bold text-bardin-teal text-sm">Your Out-of-Pocket</span>
          <span className="font-bold text-bardin-teal text-lg">{fmt(f.out_of_pocket)}</span>
        </div>
      )}

      <p className="text-xs text-slate-400 leading-relaxed">
        {f.acv_only
          ? "Because this is an ACV-only claim, insurance is not releasing recoverable depreciation. The total above is what Bardin will collect to complete the project."
          : "The first payment is collected before construction starts. The depreciation portion is released by insurance after the build is complete and invoiced."}
        {data.trade_total > 0 && ` The trades billed to you (${fmt(data.trade_total)}) are due at completion.`}
      </p>
    </div>
  );
}

function Row({
  label,
  value,
  sub,
  green,
}: {
  label: string;
  value: string;
  sub?: boolean;
  green?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between ${sub ? "pl-3" : ""}`}>
      <span className={`${sub ? "text-xs text-slate-400" : "text-sm text-slate-600"}`}>
        {label}
      </span>
      <span
        className={`font-medium ${
          green ? "text-emerald-600" : sub ? "text-xs text-slate-500" : "text-bardin-ink"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

// Oct 30, 2026 (scope gate) — the carrier lines Bardin won't do, grouped, each
// with its ACV; what they take off the deductible and what the homeowner pays
// Bardin; the depreciation note; and the acknowledgment the homeowner ticks to
// sign. The signed PDF prints the same words (installAgreementTerms.ts); the
// attorney should read them.
function WorkNotDoneCard({
  data,
  acknowledged,
  onAcknowledge,
}: {
  data: NormalizedAgreement;
  acknowledged: boolean;
  onAcknowledge: (v: boolean) => void;
}) {
  const picks = data.not_performing;
  if (picks.length === 0) return null;
  const f = data.financials;
  const groups = groupPicks(picks);
  const acvApplied = Math.min(f.acv_credit, f.deductible);
  const otherApplied = Math.max(0, f.credits_applied - acvApplied);
  const addOns = f.upgrades + f.retail_profit + f.at_cost_retail;
  const heldDep = pickTotals(picks).dep;
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-amber-200">
      <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-2">
        <span className="text-base">📋</span> {WORK_NOT_DONE_HEADING}
      </h3>
      <p className="text-xs text-slate-600 mb-3">{WORK_NOT_DONE_INTRO}</p>
      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.key}>
            <div className="flex items-center justify-between text-xs font-semibold text-bardin-ink mb-1">
              <span>{g.homeownerTitle}</span>
              <span>ACV {fmt(g.acv)}</span>
            </div>
            <ul className="space-y-1">
              {g.items.map((p, i) => {
                const meta = lineMeta(p);
                return (
                  <li key={i} className="flex items-start justify-between gap-3 bg-amber-50 rounded-lg px-3 py-2">
                    <div className="min-w-0 text-xs text-slate-700">
                      {p.n && <span className="text-[10px] text-amber-700 font-mono mr-1.5">#{p.n}</span>}
                      {p.description}
                      {meta && <span className="block text-[11px] text-slate-500">{meta}</span>}
                    </div>
                    <span className="text-xs font-medium text-bardin-ink shrink-0">{fmt(p.acv)}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 space-y-1 text-sm">
        <Row label="Your deductible" value={fmt(f.deductible)} />
        <Row label="− These items' ACV" value={`-${fmt(acvApplied)}`} sub green />
        {otherApplied > 0 && <Row label="− Other credits" value={`-${fmt(otherApplied)}`} sub green />}
        {addOns > 0 && <Row label="+ Upgrades and add-ons" value={`+${fmt(addOns)}`} />}
        <div className="border-t border-slate-300 my-1" />
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-bardin-ink">You pay Bardin before the build</span>
          <span className="text-sm font-bold text-bardin-ink">{fmt(f.out_of_pocket)}</span>
        </div>
        {f.credit_overage > 0 && (
          <p className="text-[11px] text-slate-500">Your deductible is covered in full. The other {fmt(f.credit_overage)} comes off the payment after the build.</p>
        )}
      </div>
      {heldDep > 0 && (
        <div className="mt-3 flex items-center justify-between text-xs text-slate-600">
          <span>Depreciation your carrier holds on these items</span>
          <span className="font-medium text-bardin-ink">{fmt(heldDep)}</span>
        </div>
      )}
      <p className="mt-2 text-xs text-slate-600">{WORK_NOT_DONE_DEPRECIATION}</p>
      <label className="mt-3 flex items-start gap-3 cursor-pointer rounded-lg border border-amber-200 bg-amber-50/60 p-3">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => onAcknowledge(e.target.checked)}
          className="mt-0.5 w-5 h-5 accent-bardin-teal shrink-0"
        />
        <span className="text-sm font-semibold text-bardin-ink">{WORK_NOT_DONE_ACK}</span>
      </label>
    </div>
  );
}

function ExcludedItemsCard({ data }: { data: NormalizedAgreement }) {
  if (!data.excluded_items || data.excluded_items.length === 0) return null;
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-bardin-orange/20">
      <h3 className="text-sm font-semibold text-bardin-orange flex items-center gap-2 mb-3">
        <span className="text-base">📋</span> Items Not Included
      </h3>
      <p className="text-xs text-slate-500 mb-3">
        The following insurance-approved items are NOT included in this agreement.
        Their ACV is credited back to reduce your out-of-pocket cost.
      </p>
      <div className="space-y-2">
        {data.excluded_items.map((item) => (
          <div
            key={item.line_number}
            className="flex items-center justify-between bg-orange-50 rounded-lg px-3 py-2"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[10px] text-bardin-orange font-mono shrink-0">
                #{item.line_number}
              </span>
              <span className="text-xs text-slate-600 truncate">{item.description}</span>
            </div>
            <span className="text-xs font-medium text-emerald-600 shrink-0 ml-2">
              -{fmt(item.acv)}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between bg-emerald-50 rounded-lg px-3 py-2">
        <span className="text-xs font-semibold text-emerald-700">Total Credit to You</span>
        <span className="text-sm font-bold text-emerald-700">
          -{fmt(data.financials.excluded_acv_total)}
        </span>
      </div>
    </div>
  );
}

// Oct 2, 2026 — homeowner-paid add-ons (upgrades / additional work / at-cost
// trades) with the description the RM wrote for the customer. Insurance only;
// retail deals carry their own scope of work (the trade lines print there).
// Oct 29, 2026: each trade billed to the homeowner from the trade sheet is
// listed with its price, due at completion; the card shows for it even when no
// add-on has a description.
function HomeownerAddOnsCard({ data }: { data: NormalizedAgreement }) {
  if (data.is_retail || data.add_ons.length === 0) return null;
  const hasTrades = data.add_ons.some((a) => a.dueAtCompletion);
  const hasOthers = data.add_ons.some((a) => !a.dueAtCompletion);
  if (!hasTrades && !data.add_ons.some((a) => a.description)) return null;
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-bardin-teal flex items-center gap-2 mb-3">
        <span className="text-base">➕</span> Your Add-Ons
      </h3>
      <div className="space-y-3">
        {data.add_ons.map((a, i) => (
          <div key={i} className="text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium text-bardin-ink">{a.label}</span>
              <span className="font-medium text-bardin-ink">{fmt(a.amount)}</span>
            </div>
            {a.description && <div className="text-slate-600 mt-0.5 whitespace-pre-wrap">{a.description}</div>}
            {a.dueAtCompletion && <div className="text-slate-500 text-xs mt-0.5">Trade work by our subcontractor · due at completion</div>}
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-3">
        {hasTrades
          ? hasOthers
            ? "Paid by you. Upgrades and add-ons are in your out-of-pocket above; the trades are due at completion."
            : "Paid by you, due at completion of the work."
          : "Paid by you, included in your out-of-pocket above."}
      </p>
    </div>
  );
}

function RetailAddonsCard({ data }: { data: NormalizedAgreement }) {
  if (!data.retail_addons || data.retail_addons.length === 0) return null;
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-bardin-teal flex items-center gap-2 mb-3">
        <span className="text-base">➕</span> Additional Services
      </h3>
      <div className="space-y-2">
        {data.retail_addons.map((addon, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <span className="text-slate-600">{addon.description}</span>
            <span className="font-medium text-bardin-ink">{fmt(addon.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Post-sign redirect — sends the HO to /status?tab=documents 3s after a
// successful signing so they land on their freshly-uploaded install
// agreement in the portal documents area. Manual button is the fallback if
// the auto-redirect doesn't fire (e.g. they navigated away).
// ---------------------------------------------------------------------------

function PostSignRedirect({
  deal,
  agreementNumber,
  shingleColor,
  dripEdgeColor,
}: {
  deal: { id: string; homeowner_name: string | null; property_street: string | null };
  agreementNumber: string;
  shingleColor: string;
  dripEdgeColor: string;
}) {
  // Build the token-authed deep-link to the Documents tab.
  const lastName = (deal.homeowner_name || "").split(" ").pop()?.toLowerCase() || "";
  const streetNum = (deal.property_street || "").match(/^(\d+)/)?.[1] || "";
  const token = btoa(`${deal.id}:${lastName}:${streetNum}`);
  const docsUrl = `/status?token=${encodeURIComponent(token)}&tab=documents`;

  const [seconds, setSeconds] = useState(3);

  useEffect(() => {
    // Tick the visible countdown every second.
    if (seconds <= 0) {
      window.location.assign(docsUrl);
      return;
    }
    const t = window.setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [seconds, docsUrl]);

  return (
    <div className="text-center py-12 space-y-4">
      <div className="text-5xl">✅</div>
      <h2 className="text-xl font-bold text-bardin-ink">Agreement Signed!</h2>
      <p className="text-sm text-slate-500 max-w-sm mx-auto">
        Thank you, {deal.homeowner_name?.split(" ")[0]}! Your install agreement has been received.
        Your Restore Manager will be in touch with next steps.
      </p>
      <div className="bg-bardin-sky rounded-xl p-4 max-w-xs mx-auto">
        <p className="text-xs text-bardin-teal">Agreement #{agreementNumber}</p>
        <p className="text-xs text-slate-500 mt-1">
          Signed {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </p>
        {(shingleColor && shingleColor !== "not_yet") && (
          <p className="text-xs text-slate-500 mt-1">Shingle: {shingleColor}</p>
        )}
        {(dripEdgeColor && dripEdgeColor !== "not_yet") && (
          <p className="text-xs text-slate-500">Drip Edge: {dripEdgeColor}</p>
        )}
      </div>

      {!(shingleColor && shingleColor !== "not_yet" && dripEdgeColor && dripEdgeColor !== "not_yet") && (
        <p className="text-xs text-slate-500 max-w-sm mx-auto">
          Still deciding on colors? We'll text you a link to confirm your shingle and drip edge colors before materials are ordered.
        </p>
      )}
      <p className="text-xs text-slate-400 mt-2">
        Taking you to your documents in {seconds}s...
      </p>
      <a
        href={docsUrl}
        className="inline-block bg-bardin-teal text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-bardin-teal/90 transition"
      >
        View My Documents →
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function SigningPage() {
  const auth = useHomeownerAuth();
  const { deal } = auth;

  // Form state
  const [cbExpectations, setCbExpectations] = useState(false);
  const [cbSupplement, setCbSupplement] = useState(false);
  const [cbAdditional, setCbAdditional] = useState(false);
  const [cbComms, setCbComms] = useState(false);
  // Jun 11, 2026 — retail deals: required acknowledgment of the 50% deposit /
  // 50% completion payment schedule (replaces the insurance-specific
  // supplemental-payment + communication authorizations, which don't apply).
  const [cbRetailTerms, setCbRetailTerms] = useState(false);
  // Oct 30, 2026 (scope gate): required when the agreement lists work Bardin won't do.
  const [cbNotDone, setCbNotDone] = useState(false);
  const [tcOpen, setTcOpen] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Oct 16, 2026: the signing page is the one place the homeowner interacts
  // before the build, and ~10% of built deals never get an email (it is not
  // captured at intake). Prefilled from the deal; required to sign; saved
  // through ho_update_contact_email right before the signature is recorded.
  const [contactEmail, setContactEmail] = useState("");
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const emailOk = EMAIL_RE.test(contactEmail.trim());
  useEffect(() => {
    if (deal?.homeowner_email) setContactEmail(deal.homeowner_email);
  }, [deal?.homeowner_email]);

  // Color selections. Choosing both at signing is the expected path (Oct 22,
  // 2026, per Greg): the colors print on the agreement, which is then the
  // homeowner's written color authorization. "I'll pick my colors later" is
  // the explicit opt-out; it leads to a separate Color Confirmation Addendum
  // on the /colors page before materials can be ordered.
  const [shingleColor, setShingleColor] = useState("");
  const [dripEdgeColor, setDripEdgeColor] = useState("");
  const [deferColors, setDeferColors] = useState(false);

  // Pre-fill colors from deal if already set. A drip edge value that isn't
  // one of the three options (an older hand-typed one) stays unselected so
  // the homeowner picks from the standard list.
  useEffect(() => {
    if (deal?.shingle_color) setShingleColor(deal.shingle_color);
    const drip = matchDripEdgeColor(deal?.drip_edge_color);
    if (drip) setDripEdgeColor(drip);
  }, [deal]);

  // What the agreement will say about colors — the same summary the signed
  // PDF prints in its Color Selections block.
  const colorPicks = summarizeColorSelections(shingleColor, dripEdgeColor);
  const colorsSettled = colorPicks.bothChosen || deferColors;

  // Agreement financials shown to the homeowner.
  // For UNSIGNED deals (not yet in the supps/terminal lifecycle) we render LIVE
  // numbers computed from current scope columns via buildAgreementPayload —
  // identical to what the signed PDF is generated from at sign time — so the
  // homeowner never reads a stale snapshot after a scope edit (Jun 24, 2026).
  // Once signed (or past the agreement stage) the frozen agreement_data snapshot
  // is the legal record and is shown as-is. The live payload falls back to the
  // snapshot whenever it has no meaningful scope (legacy deals missing live
  // columns). ho_resolve_deal returns the full deal row, so the cast is safe.
  const snapAgData: NormalizedAgreement | null = normalizeAgreement(deal?.agreement_data);
  const useLiveAgreement =
    !!deal &&
    deal.install_agreement_signed !== true &&
    !POST_AGREEMENT_STAGES.has((deal.stage as string) ?? "");
  const liveAgData: NormalizedAgreement | null = useLiveAgreement
    ? normalizeAgreement(buildAgreementPayload(deal as unknown as Deal))
    : null;
  const liveAgHasScope =
    !!liveAgData &&
    (liveAgData.financials.initial_invoice_amount > 0 ||
      (liveAgData.is_retail && liveAgData.retail_contract_total > 0));
  const agData: NormalizedAgreement | null = liveAgHasScope ? liveAgData : snapAgData;

  // Retail deal (Jun 11, 2026): snapshot flag is authoritative (that's what's
  // being signed); the live column is the fallback for legacy/edge cases.
  const isRetailDeal = agData?.is_retail === true || deal?.is_retail === true;

  // Non-collect revenue is no longer disclosed to or acknowledged by the
  // homeowner (Jun 19, 2026, per Greg) — it's an internal stance only.
  const notDoneCount = isRetailDeal ? 0 : agData?.not_performing.length ?? 0;
  const canSubmit = isRetailDeal
    ? cbExpectations && cbRetailTerms && emailOk && colorsSettled && !!signatureData
    : cbExpectations
      && cbSupplement
      && cbComms
      && (notDoneCount === 0 || cbNotDone)
      && emailOk
      && colorsSettled
      && !!signatureData;

  // Scope items: use agreement_data line items if present, otherwise static
  // defaults. Guard on length — the live payload (and current snapshots) carry
  // no scope_items, so an empty array must fall back to DEFAULT_SCOPE.
  const scopeItems = agData?.scope_items && agData.scope_items.length > 0
    ? agData.scope_items.map((item) => item.description)
    : DEFAULT_SCOPE;

  const productLine = agData?.product_line || "Full Roof Replacement — Owens Corning Duration TruDef";

  const agreementNumber = deal
    ? `${isRetailDeal ? "RETAIL" : deal.claim_number || "RETAIL"}-${(deal.homeowner_name || "").split(" ").pop()?.toUpperCase() || ""}`
    : "";

  // Already signed guard — boolean is the source of truth (set in the same
  // write that sets agreement_status='signed' so the OR was redundant).
  const alreadySigned = deal?.install_agreement_signed === true;

  const handleSubmit = async () => {
    if (!canSubmit || !deal) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      // 1. Upload signature image to Supabase Storage.
      //    Bucket: deal-documents (canonical bucket per CLAUDE.md). The
      //    homeowner_signing_uploads_insert/select policies (Apr 30, 2026)
      //    grant anon access to signatures/, install-agreements/, and
      //    color-addendums/ paths only. Earlier code wrote to bucket
      //    "documents" which doesn't exist on this project — every signature
      //    silently 404'd at upload time before this fix.
      let signatureUrl: string | null = null;
      let signaturePath: string | null = null;
      if (signatureData) {
        const blob = await (await fetch(signatureData)).blob();
        signaturePath = `signatures/${deal.id}/${Date.now()}-signature.png`;
        const { error: uploadErr } = await supabase.storage
          .from("deal-documents")
          .upload(signaturePath, blob, { contentType: "image/png", upsert: true });
        if (uploadErr) {
          console.error("Signature upload failed:", uploadErr);
          throw new Error(`Signature upload failed: ${uploadErr.message}`);
        }
        // Use a long-lived signed URL (1 year) so the URL works for both staff
        // (authenticated) and the homeowner (anon) when displayed in the portal.
        const { data: signed } = await supabase.storage
          .from("deal-documents")
          .createSignedUrl(signaturePath, 31536000);
        signatureUrl = signed?.signedUrl ?? null;
      }

      // 2. Derive chosen colors (null for one the homeowner deferred).
      const chosenShingle = colorPicks.shingleChosen ? colorPicks.shingle : null;
      const chosenDripEdge = colorPicks.dripChosen ? colorPicks.drip : null;

      // 2b. Save the homeowner's email when they entered or corrected it
      //     (Oct 16, 2026). The RPC format-checks it; a failure blocks the
      //     signing so the one chance to capture the address isn't lost.
      const emailTrimmed = contactEmail.trim().toLowerCase();
      if (emailTrimmed && emailTrimmed !== (deal.homeowner_email ?? "").trim().toLowerCase()) {
        const { error: emailErr } = await hoUpdateContactEmail(authTriple(deal), emailTrimmed);
        if (emailErr) throw new Error(`Couldn't save your email: ${emailErr.message}`);
      }

      // 3. Record the signing server-side via a token-validated RPC: sets
      //    agreement_status/signed flags/colors/acks, writes the signed
      //    activity_log entry, and (when colors aren't locked yet) flags the
      //    color-addendum gate + a follow-up note. Returns the fresh full deal
      //    row used by the PDF generators below — no post-sign refetch needed.
      const { data: signedDeal, error: signAgErr } = await hoSignAgreement(authTriple(deal), {
        shingle: chosenShingle,
        drip: chosenDripEdge,
        additional: cbAdditional,
        nonCollectAck: false,
        agreementNumber,
        signatureUrl,
        signaturePath,
      });
      if (signAgErr || !signedDeal) throw signAgErr ?? new Error("Signing failed. Please try again.");

      // The QuickBooks invoice is no longer requested from here (Oct 21, 2026).
      // This page called quickbooks-sync with the public key, which that
      // function now refuses; the browser's preflight had also always failed,
      // so the call never ran. The database starts it instead when the
      // agreement becomes signed (trigger deals_qbo_invoice_on_signing,
      // production QuickBooks only).

      // 4. Color follow-up text — when shingle/drip weren't locked at signing,
      //    the server texts the homeowner a link to /colors (ho-portal-notify:
      //    same wording; it checks the signed agreement, STOP and the deal's
      //    mute, and sends at most once a day). ho_sign_agreement has already
      //    flipped color_addendum_required + logged the follow-up note.
      if (!chosenShingle || !chosenDripEdge) {
        // Don't block the signing flow on a failed text — log and move on.
        const colorsText = await hoPortalNotify(authTriple(deal), "colors_needed");
        if (!colorsText.ok) console.error("Pick-colors text failed:", colorsText.code);
      }

      // 5 + 6. Generate + upload the signed install agreement PDF, then the LOA
      //   PDF, attaching each via RPC. The fresh deal row already came back from
      //   ho_sign_agreement, so no post-sign refetch is needed. Failures here
      //   don't unwind the signing event — the legal record is the signature +
      //   the signed activity_log entry; staff can regenerate either PDF later.
      const fullDeal = signedDeal as Deal;

      // 5. Install agreement PDF
      if (fullDeal) {
        try {
          const { generateInstallAgreementPDF } = await import("../../lib/installAgreementPDF");
          const pdfBlob = await generateInstallAgreementPDF(fullDeal, signatureData, agreementNumber);
          const pdfPath = `install-agreements/${deal.id}/${Date.now()}-signed.pdf`;
          const { error: pdfUploadErr } = await supabase.storage
            .from("deal-documents")
            .upload(pdfPath, pdfBlob, { contentType: "application/pdf", upsert: false });
          if (pdfUploadErr) throw pdfUploadErr;

          // 1-year signed URL — works for anon (HO portal) AND authenticated
          // (staff portal) since the URL itself is the auth token.
          const { data: signed, error: urlErr } = await supabase.storage
            .from("deal-documents")
            .createSignedUrl(pdfPath, 31536000);
          if (urlErr || !signed?.signedUrl) throw urlErr ?? new Error("createSignedUrl returned null");

          // Set install_agreement_pdf_url + log the doc, via token-validated RPC.
          await hoAttachSignedPdf(authTriple(deal), "agreement", signed.signedUrl, pdfPath, `${agreementNumber}-signed.pdf`);
        } catch (pdfErr) {
          console.error("Install agreement PDF generation failed:", pdfErr);
        }
      }

      // 6. Authorization to Communicate (LOA) — May 7, 2026.
      //    Generated whenever the HO signs (cbComms is required to sign, so
      //    it's always true here; the explicit guard is defensive in case the
      //    canSubmit gate is loosened later).
      if (cbComms && fullDeal) {
        try {
          const { generateCommunicationAuthorizationPDF } = await import("../../lib/communicationAuthorizationPDF");
          const loaBlob = await generateCommunicationAuthorizationPDF(fullDeal, signatureData);
          const loaPath = `install-agreements/${deal.id}/${Date.now()}-loa.pdf`;
          const { error: loaUploadErr } = await supabase.storage
            .from("deal-documents")
            .upload(loaPath, loaBlob, { contentType: "application/pdf", upsert: false });
          if (loaUploadErr) throw loaUploadErr;

          const { data: loaSigned, error: loaUrlErr } = await supabase.storage
            .from("deal-documents")
            .createSignedUrl(loaPath, 31536000);
          if (loaUrlErr || !loaSigned?.signedUrl) throw loaUrlErr ?? new Error("LOA createSignedUrl returned null");

          // Set communication_authorization_* + log the doc, via RPC.
          await hoAttachSignedPdf(authTriple(deal), "loa", loaSigned.signedUrl, loaPath, `${agreementNumber}-authorization-to-communicate.pdf`);
        } catch (loaErr) {
          console.error("LOA PDF generation failed:", loaErr);
        }
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Signing error:", err);
      setSubmitError("Something went wrong. Please try again or call us at (470) 823-8816.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <HomeownerGate {...auth} title="Installation Agreement">
      {/* Already signed */}
      {deal && alreadySigned && !submitted && (
        <div className="text-center py-12 space-y-4">
          <div className="text-5xl">✅</div>
          <h2 className="text-xl font-bold text-bardin-ink">Agreement Already Signed</h2>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">
            This agreement was signed on{" "}
            {deal.install_agreement_signed_date
              ? new Date(deal.install_agreement_signed_date).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })
              : "a previous date"}
            . If you have questions, contact your Restore Manager or call us.
          </p>
          <div className="bg-bardin-sky rounded-xl p-4 max-w-xs mx-auto">
            <p className="text-xs text-bardin-teal">Agreement #{agreementNumber}</p>
          </div>
        </div>
      )}

      {/* Signing form */}
      {deal && !alreadySigned && !submitted && (
        <div className="space-y-4 pb-8">
          {/* Agreement Badge */}
          <div className="bg-bardin-ink text-white text-center py-2 rounded-lg text-sm font-medium">
            Agreement <span className="font-bold">#{agreementNumber}</span>
          </div>

          {/* Parties — legal entity named once; "Company" everywhere after */}
          <p className="text-xs text-slate-600 leading-relaxed px-1">
            {agreementPartiesPreamble(isRetailDeal)}
          </p>

          {/* Homeowner Info */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-bardin-teal flex items-center gap-2 mb-3">
              <span className="text-base">👤</span> Homeowner Information
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-slate-400">Homeowner</div>
                <div className="font-medium text-bardin-ink">{deal.homeowner_name}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">{isRetailDeal ? "Contract Type" : "Claim #"}</div>
                <div className="font-medium text-bardin-ink">{isRetailDeal ? "Retail" : deal.claim_number || "N/A"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-xs text-slate-400">Property Address</div>
                <div className="font-medium text-bardin-ink">
                  {deal.property_street}
                  {deal.property_city && `, ${deal.property_city}`}
                  {deal.property_state && `, ${deal.property_state}`}
                  {deal.property_zip && ` ${deal.property_zip}`}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Insurance</div>
                <div className="font-medium text-bardin-ink">{isRetailDeal ? "N/A — retail job" : deal.insurance_company || "N/A"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Date</div>
                <div className="font-medium text-bardin-ink">
                  {new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}
                </div>
              </div>
            </div>
          </div>

          {/* Scope of Work */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-bardin-teal flex items-center gap-2 mb-3">
              <span className="text-base">✅</span> Scope of Work
            </h3>
            <p className="text-sm font-semibold text-bardin-ink mb-3">{productLine}</p>
            {isRetailDeal && agData?.retail_scope_description && (
              <p className="text-sm text-slate-600 mb-3 whitespace-pre-wrap">{agData.retail_scope_description}</p>
            )}
            {isRetailDeal && Array.isArray((agData as { retail_items?: Array<{ label?: string; kind?: string; amount?: number }> } | null)?.retail_items) &&
              (((agData as { retail_items?: unknown[] } | null)?.retail_items?.length ?? 0) > 0) && (
              <div className="mb-3 rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                {((agData as { retail_items?: Array<{ label?: string; kind?: string; amount?: number }> }).retail_items ?? []).map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                    <span className="text-slate-700">{(it.label && it.label.trim()) || (it.kind === "roof" ? "Roof replacement" : "Additional work")}</span>
                    <span className="font-medium text-bardin-ink">
                      {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(it.amount) || 0)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {/* Oct 29, 2026 — trades billed to the homeowner, after the retail
                items: on top of the contract total, due at completion. */}
            {isRetailDeal && agData && agData.trade_lines.length > 0 && (
              <div className="mb-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Trades billed to you (due at completion)
                </div>
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                  {agData.trade_lines.map((t, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="text-slate-700">{t.label}</span>
                      <span className="font-medium text-bardin-ink">
                        {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(t.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(colorPicks.shingleChosen || colorPicks.dripChosen) && (
              <p className="text-sm text-slate-600 mb-3">
                Shingle: <span className="font-medium text-bardin-ink">{colorPicks.shingle}</span>
                <span className="text-slate-400"> · </span>
                Drip edge: <span className="font-medium text-bardin-ink">{colorPicks.drip}</span>
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {scopeItems.map((item) => (
                <span
                  key={item}
                  className="bg-bardin-sky text-bardin-teal text-xs font-medium px-3 py-1.5 rounded-full"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Work Bardin won't do (Oct 30, 2026), or the older excluded-items list */}
          {agData && notDoneCount > 0
            ? <WorkNotDoneCard data={agData} acknowledged={cbNotDone} onAcknowledge={setCbNotDone} />
            : agData && <ExcludedItemsCard data={agData} />}

          {/* Retail Add-Ons (conditional) */}
          {agData && <HomeownerAddOnsCard data={agData} />}
          {agData && <RetailAddonsCard data={agData} />}

          {/* Color Selection */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-bardin-teal flex items-center gap-2 mb-3">
              <span className="text-base">🎨</span> Color Selection{" "}
              <span className="text-xs font-normal text-slate-400">(required)</span>
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Your shingle and drip edge colors are written into the agreement you're signing — that is your authorization to order them.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-bardin-ink mb-1">
                  Shingle Color
                </label>
                <select
                  value={shingleColor}
                  onChange={(e) => setShingleColor(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent bg-white"
                >
                  <option value="">Select a color...</option>
                  {SHINGLE_COLORS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-bardin-ink mb-1">
                  Drip Edge Color
                </label>
                <select
                  value={dripEdgeColor}
                  onChange={(e) => setDripEdgeColor(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent bg-white"
                >
                  <option value="">Select a color...</option>
                  {DRIP_EDGE_COLORS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">Drip edge comes in white, black or brown.</p>
              </div>

              {/* Opt-out: a separate Color Confirmation Addendum follows. */}
              {!colorPicks.bothChosen && (
                <label className="flex items-start gap-3 cursor-pointer border-t border-slate-100 pt-3">
                  <input
                    type="checkbox"
                    checked={deferColors}
                    onChange={(e) => setDeferColors(e.target.checked)}
                    className="mt-1 w-5 h-5 accent-bardin-teal shrink-0"
                  />
                  <div className="text-sm">
                    <span className="font-semibold text-bardin-ink">I'll pick my colors later</span>
                    <span className="text-slate-500 block text-xs mt-0.5">
                      We'll text you a link to confirm your shingle and drip edge colors on a separate Color Confirmation Addendum. Materials can't be ordered until it's signed.
                    </span>
                  </div>
                </label>
              )}
            </div>
          </div>

          {/* Financial Summary (from agreement_data or deal fields) */}
          {agData ? (
            <FinancialSummaryCard data={agData} />
          ) : deal.acv_amount ? (
            /* Fallback: show basic financials from deal columns */
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-bardin-teal flex items-center gap-2 mb-4">
                <span className="text-base">💰</span> Financial Summary
              </h3>
              <div className="space-y-2 text-sm">
                <Row label="Actual Cash Value (ACV)" value={fmt(deal.acv_amount)} />
                <Row label="Your Deductible" value={fmt(deal.deductible)} />
                <div className="border-t-2 border-bardin-teal/20 my-1" />
                <div className="bg-bardin-sky rounded-lg px-3 py-2.5 flex items-center justify-between">
                  <span className="font-bold text-bardin-teal text-sm">Your Out-of-Pocket</span>
                  <span className="font-bold text-bardin-teal text-lg">{fmt(deal.deductible)}</span>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed mt-2">
                  Insurance pays the remaining balance directly to you after completion.
                  You then pay Company per the terms of this agreement.
                </p>
              </div>
            </div>
          ) : null}

          {/* What to Expect */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-bardin-teal flex items-center gap-2 mb-3">
              <span className="text-base">📋</span> What to Expect
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {EXPECTATIONS.map((exp) => (
                <div
                  key={exp.title}
                  className="flex items-start gap-3 bg-slate-50 rounded-lg p-3"
                >
                  <span className="text-xl shrink-0">{exp.icon}</span>
                  <div>
                    <div className="text-xs font-semibold text-bardin-ink">{exp.title}</div>
                    <div className="text-xs text-slate-500 leading-relaxed">{exp.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            {/* Full guide (Oct 16, 2026): the public what-to-expect page at
                public/build-day — same site, no token needed. */}
            <a
              href="/build-day"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-bardin-orange hover:underline"
            >
              Read the full build-day guide, with pictures →
            </a>
          </div>

          {/* Warranty */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-start gap-3 bg-gradient-to-br from-bardin-sky to-blue-50 p-4 rounded-lg">
              <div className="w-10 h-10 bg-bardin-teal rounded-full flex items-center justify-center shrink-0">
                <span className="text-white text-lg">🛡</span>
              </div>
              <div className="text-sm text-bardin-teal">
                <strong className="block text-sm">Owens Corning Preferred Protection</strong>
                Your roof is backed by a manufacturer warranty from Owens Corning, plus
                Company's 2-year workmanship warranty covering installation quality.
              </div>
            </div>
          </div>

          {/* Contact email (Oct 16, 2026) — the signed agreement, invoice and
              warranty certificate go here, so it's required to sign. */}
          <div className="bg-white rounded-xl p-5 shadow-sm space-y-2">
            <h3 className="text-sm font-semibold text-bardin-teal flex items-center gap-2 mb-1">
              <span className="text-base">📧</span> Your Email
            </h3>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="you@example.com"
              className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-teal/30 ${
                contactEmail.trim() && !emailOk ? "border-red-300" : "border-slate-300"
              }`}
            />
            <p className="text-xs text-slate-500">
              We'll send your signed agreement, your invoice and your warranty certificate here.
              {contactEmail.trim() && !emailOk && (
                <span className="block text-red-600 mt-1">That email doesn't look right.</span>
              )}
            </p>
          </div>

          {/* Checkboxes */}
          <div className="bg-white rounded-xl p-5 shadow-sm space-y-4">
            <h3 className="text-sm font-semibold text-bardin-teal flex items-center gap-2 mb-1">
              <span className="text-base">✍️</span> Acknowledgments
            </h3>

            {/* CB 1: Expectations (required) */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cbExpectations}
                onChange={(e) => setCbExpectations(e.target.checked)}
                className="mt-1 w-5 h-5 accent-bardin-teal shrink-0"
              />
              <div className="text-sm">
                <span className="font-semibold text-bardin-ink">I Understand the Expectations</span>
                <span className="text-slate-500 block text-xs mt-0.5">
                  Company did not build my home and has not maintained my property.
                  A roof replacement is a large project that may reveal or accelerate existing
                  maintenance needs. This is normal and is not caused by Company's work.
                  I have reviewed and understand the project expectations outlined above.
                </span>
              </div>
            </label>

            {/* Retail payment-terms acknowledgment (required on retail deals).
                Replaces the insurance-specific checkboxes below, which are
                hidden when isRetailDeal. Jun 11, 2026. */}
            {isRetailDeal && (
              <>
                <div className="border-t border-slate-100" />
                <label className="flex items-start gap-3 cursor-pointer bg-teal-50 border border-teal-200 rounded-lg p-3 -mx-1">
                  <input
                    type="checkbox"
                    checked={cbRetailTerms}
                    onChange={(e) => setCbRetailTerms(e.target.checked)}
                    className="mt-1 w-5 h-5 accent-bardin-teal shrink-0"
                  />
                  <div className="text-sm">
                    <span className="font-semibold text-bardin-ink">
                      Payment Schedule Agreement{" "}
                      <span className="text-xs font-normal text-teal-700">(required)</span>
                    </span>
                    <span className="text-slate-600 block text-xs mt-0.5">
                      I agree to pay Company the Contract Total shown in the Financial Summary:
                      a <span className="font-semibold">50% deposit</span> due at signing or before
                      material delivery, and the <span className="font-semibold">remaining 50%</span>{" "}
                      due upon completion of the work
                      {(agData?.trade_total ?? 0) > 0 && (
                        <>, plus the <span className="font-semibold">trades billed to me ({fmt(agData?.trade_total ?? 0)})</span>, due at completion</>
                      )}
                      . See Terms §2 for the full clause.
                    </span>
                  </div>
                </label>
              </>
            )}

            {!isRetailDeal && (<>
            <div className="border-t border-slate-100" />

            {/* CB 2: Supplemental payments (required) */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cbSupplement}
                onChange={(e) => setCbSupplement(e.target.checked)}
                className="mt-1 w-5 h-5 accent-bardin-teal shrink-0"
              />
              <div className="text-sm">
                <span className="font-semibold text-bardin-ink">Supplemental Payment Authorization</span>
                <span className="text-slate-500 block text-xs mt-0.5">
                  I understand that my insurance company may issue supplemental payments after
                  the initial claim. I agree to promptly endorse and forward any supplemental
                  checks to Company for work already completed. All approved supplement
                  proceeds become part of this contract.
                </span>
              </div>
            </label>

            <div className="border-t border-slate-100" />

            {/* CB 3: Additional damage (optional) */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cbAdditional}
                onChange={(e) => setCbAdditional(e.target.checked)}
                className="mt-1 w-5 h-5 accent-bardin-teal shrink-0"
              />
              <div className="text-sm">
                <span className="font-semibold text-bardin-ink">
                  Additional Storm Damage Authorization{" "}
                  <span className="text-xs font-normal text-slate-400">(optional)</span>
                </span>
                <span className="text-slate-500 block text-xs mt-0.5">
                  During the roof replacement, Company may discover additional storm damage
                  (gutters, siding, fencing, etc.) not in your original claim. By checking this box,
                  you authorize Company to submit supplemental claims for this additional damage
                  and complete the approved work.{" "}
                  <span className="text-bardin-orange font-semibold">
                    If an additional supplement is approved and you choose not to have Company complete
                    that work, a 20% processing fee applies to the approved amount.
                  </span>
                </span>
              </div>
            </label>

            {/* Non-collect acknowledgment removed Jun 19, 2026 (per Greg) —
                non-collect revenue is no longer disclosed to the homeowner. */}

            <div className="border-t border-slate-100" />

            {/* CB 4: Communication authorization (required).
                Checking this box generates a separate one-page Authorization to
                Communicate (LOA) PDF when you sign — a standalone document
                Company can send to your insurance company's claims department.
                See generateCommunicationAuthorizationPDF in handleSubmit. */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cbComms}
                onChange={(e) => setCbComms(e.target.checked)}
                className="mt-1 w-5 h-5 accent-bardin-teal shrink-0"
              />
              <div className="text-sm">
                <span className="font-semibold text-bardin-ink">Communication Authorization</span>
                <span className="text-slate-500 block text-xs mt-0.5">
                  I authorize Company to communicate with my insurance company on my behalf
                  regarding this claim and any related supplements. I agree to allow Company
                  to perform work in accordance with the type and amount approved by the insurance company.
                </span>
                <span className="text-bardin-teal block text-xs mt-1.5 font-medium">
                  When you sign, this generates a separate one-page Authorization document Company can send to your insurance company.
                </span>
              </div>
            </label>
            </>)}
          </div>

          {/* Terms & Conditions */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <button
              onClick={() => setTcOpen(!tcOpen)}
              className="flex items-center justify-between w-full text-sm font-semibold text-bardin-teal"
            >
              <span className="flex items-center gap-2">
                <span className="text-base">📄</span> Terms & Conditions
              </span>
              <svg
                className={`w-4 h-4 text-slate-400 transition-transform ${tcOpen ? "rotate-180" : ""}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {tcOpen && (
              <div className="mt-3 space-y-4 text-xs text-slate-600 leading-relaxed">
                {(isRetailDeal ? RETAIL_AGREEMENT_TERMS : INSTALL_AGREEMENT_TERMS).map((section) => (
                  <div key={section.heading}>
                    <p className="font-semibold text-bardin-ink mb-1">{section.heading}</p>
                    {section.body.split(/\n\n+/).map((para, i) => (
                      <p key={i} className={i > 0 ? "mt-1.5" : ""}>{para}</p>
                    ))}
                  </div>
                ))}
                <p className="text-[10px] text-slate-400 pt-2 border-t border-slate-200">
                  This is the complete Terms &amp; Acknowledgment section. The signed PDF you receive after signing contains identical language plus the Scope of Work and Financial Summary on page 1.
                </p>
              </div>
            )}
          </div>

          {/* Signature */}
          <div className="bg-white rounded-xl p-5 shadow-sm space-y-3">
            <h3 className="text-sm font-semibold text-bardin-teal flex items-center gap-2">
              <span className="text-base">✒️</span> Your Signature
            </h3>
            <SignatureCanvas
              onSign={(dataUrl) => setSignatureData(dataUrl)}
              onClear={() => setSignatureData(null)}
              defaultTypedName={deal.homeowner_name || ""}
            />
            <p className="text-xs text-slate-400 text-center">
              {deal.homeowner_name} &nbsp;|&nbsp;{" "}
              {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </p>

            {submitError && (
              <div className="bg-red-50 text-red-600 text-xs rounded-lg px-3 py-2 text-center">
                {submitError}
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className={`w-full py-3.5 rounded-xl font-semibold text-white text-sm transition ${
                canSubmit
                  ? "bg-bardin-orange hover:bg-bardin-orange/90 cursor-pointer"
                  : "bg-slate-300 cursor-not-allowed"
              }`}
            >
              {submitting ? "Signing..." : "Sign Agreement"}
            </button>

            {!canSubmit && (
              <p className="text-xs text-slate-400 text-center">
                {notDoneCount > 0 && !cbNotDone
                  ? "Please tick the box under \"Work Bardin won't do\" above, check all required boxes and sign to continue."
                  : !colorsSettled
                  ? "Please pick your shingle and drip edge colors above (or tick \"I'll pick my colors later\"), check all required boxes and sign to continue."
                  : emailOk
                    ? "Please check all required boxes and sign above to continue."
                    : "Please enter your email, check all required boxes and sign above to continue."}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-slate-400 pt-2 pb-4 leading-relaxed">
            <strong>Bardin Roofing</strong>
            <br />
            <span className="text-[10px]">
              Peach Duck Contracting Company, S Corp. d/b/a Bardin Roofing
            </span>
            <br />
            225 Reformation Parkway, Canton, GA 30114
            <br />
            (470) 823-8816 |{" "}
            <a href="https://www.bardinroofing.com" className="text-bardin-teal">
              bardinroofing.com
            </a>
          </div>
        </div>
      )}

      {/* Success */}
      {deal && submitted && <PostSignRedirect deal={deal} agreementNumber={agreementNumber} shingleColor={shingleColor} dripEdgeColor={dripEdgeColor} />}
    </HomeownerGate>
  );
}
